import { format, isValid, parseISO } from "date-fns";
import {
  formatTrackingAmount,
  formatTrackingDate,
  isDonationApproved,
} from "./donationTrackingFormatters";

export const DONOR_DIRECTORY_PAGE_SIZE = 10;

export function defaultDirectoryFilters() {
  return { search: "", column: "", value: "" };
}

export function formatDirectoryDate(value) {
  if (!value) return "—";
  const d = parseISO(String(value));
  return isValid(d) ? format(d, "PP") : String(value);
}

export function paginateList(items, page, pageSize = DONOR_DIRECTORY_PAGE_SIZE) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    totalPages,
    total,
    slice: items.slice(start, start + pageSize),
  };
}

export function filterDirectoryRows(rows, filters, { getSearchableText, getColumnValue }) {
  let result = Array.isArray(rows) ? [...rows] : [];
  const search = String(filters?.search ?? "")
    .trim()
    .toLowerCase();
  if (search) {
    result = result.filter((row) =>
      getSearchableText(row).toLowerCase().includes(search)
    );
  }
  const col = String(filters?.column ?? "").trim();
  const val = String(filters?.value ?? "").trim();
  if (col && val) {
    result = result.filter((row) => {
      const cell = getColumnValue(col, row);
      return String(cell ?? "").trim().toLowerCase() === val.toLowerCase();
    });
  }
  return result;
}

export function buildValueOptionsFromRows(rows, columnId, getColumnValue) {
  const seen = new Set();
  const opts = [];
  for (const row of rows || []) {
    const raw = getColumnValue(columnId, row);
    if (raw == null || raw === "" || raw === "—") continue;
    const label = String(raw);
    if (!seen.has(label)) {
      seen.add(label);
      opts.push({ value: label, label });
    }
  }
  return opts.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" })
  );
}

export function loadColumnVisibility(storageKey, columnDefs) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialColumnVisibility(storageKey, columnDefs) {
  const saved = loadColumnVisibility(storageKey, columnDefs);
  const initial = {};
  for (const c of columnDefs) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

/* —— Donation history —— */

export const DONATION_HISTORY_COLUMN_DEFS = [
  { id: "donation_date", label: "Date", defaultVisible: true },
  { id: "donation_amount", label: "Amount", defaultVisible: true },
  { id: "purpose_name", label: "Purpose", defaultVisible: true },
  { id: "receipt_no", label: "Receipt No", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
];

export const DONATION_HISTORY_VISIBILITY_KEY =
  "girinestham.donorPortal.donationHistory.columnVisibility";

export function historyCellValue(colId, row) {
  switch (colId) {
    case "donation_date":
      return formatDirectoryDate(row.donation_date);
    case "donation_amount":
      return formatTrackingAmount(row.donation_amount);
    case "purpose_name":
      return row.purpose_name || "—";
    case "receipt_no":
      return row.receipt_no || "—";
    case "status":
      return row.status || "—";
    default:
      return row?.[colId] ?? "—";
  }
}

export function historySearchableText(row) {
  return [
    row.receipt_no,
    row.purpose_name,
    row.status,
    row.donation_amount,
    formatDirectoryDate(row.donation_date),
  ]
    .filter(Boolean)
    .join(" ");
}

/* —— Donation tracking: cash —— */

export const TRACKING_DONATIONS_COLUMN_DEFS = [
  { id: "donation_date", label: "Date", defaultVisible: true },
  { id: "donation_amount", label: "Amount", defaultVisible: true },
  { id: "purpose_name", label: "Purpose", defaultVisible: true },
  { id: "donation_mode", label: "Mode", defaultVisible: true },
  { id: "receipt_no", label: "Receipt", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
];

export const TRACKING_DONATIONS_VISIBILITY_KEY =
  "girinestham.donorPortal.trackingDonations.columnVisibility";

export function trackingDonationCellValue(colId, row) {
  switch (colId) {
    case "donation_date":
      return formatTrackingDate(row.donation_date);
    case "donation_amount":
      return formatTrackingAmount(row.donation_amount);
    case "purpose_name":
      return row.purpose_name || "—";
    case "donation_mode":
      return row.donation_mode || "—";
    case "receipt_no":
      return row.receipt_no && row.receipt_no !== "-" ? row.receipt_no : "—";
    case "status":
      return isDonationApproved(row) ? "Approved" : "Pending";
    default:
      return row?.[colId] ?? "—";
  }
}

export function trackingDonationSearchableText(row) {
  return [
    row.receipt_no,
    row.purpose_name,
    row.donation_mode,
    trackingDonationCellValue("status", row),
    formatTrackingAmount(row.donation_amount),
    formatTrackingDate(row.donation_date),
  ]
    .filter(Boolean)
    .join(" ");
}

/* —— Donation tracking: equipment —— */

export const TRACKING_EQUIPMENT_COLUMN_DEFS = [
  { id: "equipment_code", label: "Code", defaultVisible: true },
  { id: "equipment_name", label: "Item", defaultVisible: true },
  { id: "donation_date", label: "Date", defaultVisible: true },
  { id: "quantity", label: "Qty", defaultVisible: true },
  { id: "estimated_value", label: "Est. value", defaultVisible: true },
  { id: "category_name", label: "Category", defaultVisible: true },
  { id: "status", label: "Verification", defaultVisible: true },
];

export const TRACKING_EQUIPMENT_VISIBILITY_KEY =
  "girinestham.donorPortal.trackingEquipment.columnVisibility";

export function trackingEquipmentCellValue(colId, row) {
  switch (colId) {
    case "donation_date":
      return formatTrackingDate(row.donation_date);
    case "estimated_value":
      return formatTrackingAmount(row.estimated_value);
    case "status":
      return Number(row.donation_verified) === 1 ? "Verified" : "Pending";
    case "equipment_code":
      return row.equipment_code || "—";
    case "equipment_name":
      return row.equipment_name || "—";
    case "quantity":
      return row.quantity ?? "—";
    case "category_name":
      return row.category_name || "—";
    default:
      return row?.[colId] ?? "—";
  }
}

export function trackingEquipmentSearchableText(row) {
  return [
    row.equipment_code,
    row.equipment_name,
    row.category_name,
    trackingEquipmentCellValue("status", row),
    formatTrackingAmount(row.estimated_value),
  ]
    .filter(Boolean)
    .join(" ");
}
